import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Drawer } from '../../../components/ui/Drawer';
import { Select } from '../../../components/ui/Select';
import { FacetPanel } from './FacetPanel';
import { ItemsGrid } from './ItemsGrid';
import { SORT_OPTIONS, asSort, type InventoryFilterValues } from './inventoryFilters';
import { useInventory } from './useInventory';

interface Props {
  packId: number;
  filters: InventoryFilterValues;
  page: number;
  /** Bumped after a successful add so the picker drops the cards it just gave away. */
  version: number;
  currency: string | null;
  maxPerRequest: number;
  busy: boolean;
  readOnly?: boolean;
  readOnlyHint?: string;
  onFiltersChange: (next: InventoryFilterValues) => void;
  onPageChange: (page: number) => void;
  onAdd: (ids: number[]) => void;
  onClose: () => void;
}

/**
 * Add cards to the pack, over the Elasticsearch inventory: filters, facets and
 * "Group duplicates" live here because this is the only screen that searches.
 *
 * A right-edge drawer, not a route: the operator is picking cards *for the pack
 * they are looking at*, the contents stay visible beside it and one Escape
 * away, and the facet column gets room to breathe. Mounting is what opens
 * it, so the search only runs while the mode is open; the filters themselves
 * still live in the URL, so a reload or a shared link lands on the same search.
 */
export function AddItemsModal({
  packId,
  filters,
  page,
  version,
  currency,
  maxPerRequest,
  busy,
  readOnly = false,
  readOnlyHint,
  onFiltersChange,
  onPageChange,
  onAdd,
  onClose,
}: Props) {
  const [selected, setSelected] = useState<number[]>([]);
  const { data, loading, error } = useInventory(packId, filters, page, version);

  // Selection belongs to one result set — any new page, filter or add drops it.
  useEffect(() => {
    setSelected([]);
  }, [filters, page, version]);

  return (
    <Drawer open title="Add cards" onClose={onClose}>
      <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
        <FacetPanel
          filters={filters}
          facets={data?.facets ?? null}
          fmv={data?.fmv ?? null}
          onChange={onFiltersChange}
        />

        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-2">
            <label className="relative min-w-[200px] flex-1">
              <Search
                size={14}
                aria-hidden="true"
                className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--co-muted)]"
              />
              <input
                type="search"
                aria-label="Search cards"
                placeholder="Search by player, set, title…"
                value={filters.q}
                onChange={(e) => onFiltersChange({ ...filters, q: e.target.value })}
                className="w-full rounded-lg border border-[var(--co-border)] bg-[var(--co-surface)] py-1.5 pl-7 pr-2 text-[12px] text-[var(--co-text)] outline-none placeholder:text-[var(--co-muted)] focus:border-[var(--co-brand)]"
              />
            </label>
            <Select
              aria-label="Sort"
              value={filters.sort}
              options={SORT_OPTIONS}
              onChange={(v) => onFiltersChange({ ...filters, sort: asSort(v) })}
            />
            <Checkbox
              aria-label="Group duplicates"
              label="Group duplicates"
              checked={filters.group_variations}
              onChange={(v) => onFiltersChange({ ...filters, group_variations: v })}
            />
            {data && (
              <span className="ml-auto text-[11px] text-[var(--co-muted)]">
                {`${data.meta.total_count} available`}
              </span>
            )}
          </div>

          <ItemsGrid
            items={data?.items ?? []}
            loading={loading}
            error={error}
            meta={data?.meta ?? null}
            page={page}
            onPageChange={onPageChange}
            selected={selected}
            onSelectedChange={setSelected}
            currency={currency}
            busy={busy}
            maxPerRequest={maxPerRequest}
            readOnly={readOnly}
            {...(readOnlyHint ? { readOnlyHint } : {})}
            onAdd={onAdd}
          />
        </div>
      </div>
    </Drawer>
  );
}
